import { useEffect, useState, type MouseEvent } from 'react';
import { CheckIcon, CopyIcon, ExternalLinkIcon } from 'lucide-react';
import { companyRequest } from '@/company/api';
import { API_BASE } from '@/lib/shell';
import { interpolateCopy, useChatCopy, useChatInterfaceLocale } from '@/lib/chat-i18n';
import { safeSourceUrl } from '@/workspace/safe-url';
import type { CitationExcerpt, CitationTarget } from './reference';

export function CitationExcerptView({
  target,
  onOpenSource,
}: {
  target: CitationTarget;
  onOpenSource?: (event: MouseEvent<HTMLAnchorElement>) => void;
}) {
  const copy = useChatCopy();
  const locale = useChatInterfaceLocale();
  const [result, setResult] = useState<{
    key: string;
    value?: CitationExcerpt;
    failed?: boolean;
  }>();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    let active = true;
    companyRequest<CitationExcerpt>(target.endpoint).then(
      (value) => {
        if (active) setResult({ key: target.endpoint, value });
      },
      () => {
        if (active) setResult({ key: target.endpoint, failed: true });
      },
    );
    return () => {
      active = false;
    };
  }, [target.endpoint]);
  const current = result?.key === target.endpoint ? result : undefined;
  if (!current)
    return (
      <p className="pc-citation-state" role="status">
        {copy.citationLoading}
      </p>
    );
  if (!current.value)
    return (
      <p className="pc-citation-state" role="status">
        {copy.citationUnavailable}
      </p>
    );
  const value = current.value;
  const original = safeSourceUrl(value.sourceUrl);
  const date = value.date ? new Date(value.date) : null;
  const dateLabel =
    date && Number.isFinite(date.getTime())
      ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date)
      : null;
  async function copyCitation() {
    try {
      await navigator.clipboard.writeText(
        `${value!.title}\n\n${value!.text}\n\n${new URL(target.href, API_BASE || window.location.href).href}`,
      );
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }
  return (
    <>
      <h3 className="pc-citation-title">{value.title}</h3>
      <p className="pc-citation-meta">
        {[
          dateLabel,
          value.revision ? `v${value.revision}` : null,
          value.pages
            ? interpolateCopy(copy.citationPages, {
                first: String(value.pages.first),
                last: String(value.pages.last),
              })
            : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {original && (
        <a
          className="pc-citation-publisher"
          href={original}
          target="_blank"
          rel="noopener noreferrer"
        >
          {new URL(original).hostname}
          <ExternalLinkIcon size={12} />
        </a>
      )}
      {value.coverage && <p className="pc-citation-coverage">{value.coverage}</p>}
      <blockquote className="pc-citation-excerpt">
        <mark>{value.text}</mark>
      </blockquote>
      <div className="pc-citation-actions">
        <a href={target.href} onClick={onOpenSource}>
          <ExternalLinkIcon size={14} />
          {copy.citationOpen}
        </a>
        <button
          type="button"
          onClick={copyCitation}
          aria-label={copied ? copy.citationCopied : copy.copy}
        >
          {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          {copied ? copy.citationCopied : copy.copy}
        </button>
      </div>
    </>
  );
}
